/**
 * What a report cannot say yet, and who fills it: one hint per hole with a
 * Dig button that carries the directive. Shared by every report so the copy
 * and the button behave the same everywhere.
 */
import { useState } from 'react';
import type { BoardroomReportMissing, BoardroomTableDig } from '../../../types';

import { useSeen, type SeenMarks } from '../useSeen';

export type DigIntent = 'WIDER' | 'DEEPER';
/** Who to wake and what to hand them: a FOCUSED run. Null for a wider dig
 *  that has no one item to point at. */
export interface DigFocus {
  archetype: string;
  /** One item to hand the persona, or null for a whole table. */
  item_id: number | null;
  /** A table dig is the room's budget, not the small focused one. */
  scope?: 'item' | 'table';
}
export type OnDig = (directive: string, intent: DigIntent, focus: DigFocus | null) => void;
export const LIST_INTENT: Record<string, DigIntent> = { players: 'WIDER', pain_list: 'WIDER', idea_list: 'WIDER' };
/** Up to this many rows keep a Dig button each; past it, one button digs
 *  into them all. Forty identical buttons down a page is a wall, not a choice. */
export const ROW_BUTTONS_MAX = 3;

/** One dig for the whole list: who fills these, and what to tell them if no
 *  brief can be written. The room turns a table dig into a job per seat, each
 *  with its own items to work through in order. Pure. */
export function digIntoThese(missing: BoardroomReportMissing[]): {
  directive: string;
  intent: DigIntent;
  focus: DigFocus;
} {
  const who = Array.from(new Set(missing.map(m => m.who).filter(Boolean)));
  const intent: DigIntent = missing.every(m => LIST_INTENT[m.what] === 'WIDER') ? 'WIDER' : 'DEEPER';
  const lines = missing.slice(0, 40).map(m => (m.item_id ? `#${m.item_id} ${m.hint}` : m.hint));
  return {
    directive: `Work through these, one at a time, in this order: ${lines.join('; ')}.`,
    intent,
    focus: { archetype: who.join(','), item_id: null, scope: 'table' },
  };
}

const noop = () => {};

export default function MissingList({
  missing,
  empty,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  seen: given,
}: {
  missing: BoardroomReportMissing[];
  /** The whole report is empty: the heading says so instead of "cannot say yet". */
  empty: boolean;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at: a question stays marked
   *  until it has actually been on screen, however long the tab was shut. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** THE REPORT'S OWN TRACKER. This list hangs UNDER every report, and its
   *  rows are the handful of questions still open — so a tracker of its own
   *  pruned every mark in the report as "no row here" the instant the tab was
   *  opened. That is the four on the tab that opened to nothing lit and no
   *  button to press. */
  seen?: SeenMarks;
}) {
  // A QUESTION APPEARING IS NEWS TOO. These come and go as the room works —
  // one gets answered, three more are found — and nothing marked the change.
  // Hooks run before any early return, or React sees a different number of
  // them between renders.
  const own = useSeen(
    given ? undefined : unseen,
    given ? noop : (onSeen ?? noop),
    given ? undefined : missing.map(m => m.item_id ?? -1),
  );
  const seen = given ?? own;
  // A LONG LIST IS ONE LINE until asked for. A hundred and forty-eight
  // sentences under the table pushed it off the screen, and the one button
  // that works through them all says the same thing.
  const [open, setOpen] = useState(false);
  if (missing.length === 0) return null;
  const many = missing.length > ROW_BUTTONS_MAX;
  const folded = many && !open;
  return (
    <section className="shrink-0 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-3" data-testid="missing">
      <div className={`${folded ? '' : 'mb-1.5 '}flex items-center gap-2`}>
        <h3 className={`flex-1 text-xs text-gray-400 ${many ? '' : 'uppercase tracking-wide'}`}>
          {many
            ? `${missing.length} gaps${empty ? ': nothing to show yet' : ' the table cannot fill yet'}`
            : empty
              ? 'Nothing to show yet'
              : 'What the table cannot say yet'}
        </h3>
        {many && (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="shrink-0 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
            data-testid="missing-toggle"
          >
            {open ? 'Hide the list' : 'Show the list'}
          </button>
        )}
        {many && (
          <button
            type="button"
            disabled={digging}
            onClick={() => {
              const d = digIntoThese(missing);
              onDig(d.directive, d.intent, d.focus);
            }}
            title={
              digging
                ? 'A dig is going; this will be there when it ends'
                : 'One dig: each seat gets its own share of these to work through, one at a time. You see the plan before anything is spent.'
            }
            className="shrink-0 px-2 py-0.5 rounded border border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50 text-xs"
            data-testid="dig-into-these"
          >
            {digging ? 'a dig is going' : 'Fill them'}
          </button>
        )}
      </div>
      {!folded && (
      <ul className="flex flex-col gap-1.5">
        {missing.slice(0, 40).map(m => {
          const intent = LIST_INTENT[m.what] ?? 'DEEPER';
          return (
            <li
              key={`${m.item_id}-${m.what}`}
              ref={m.item_id ? seen.mark(m.item_id) : undefined}
              className={`flex items-start gap-2 text-xs ${
                seen.cls(m.item_id ?? -1) ? `${seen.cls(m.item_id ?? -1)} rounded px-1` : ''
              }`}
            >
              <span className="flex-1 text-gray-600 dark:text-gray-300">
                {m.item_id ? (
                  <button type="button" onClick={() => onOpen(m.item_id!)} className="hover:underline text-left">
                    {m.hint}
                  </button>
                ) : (
                  m.hint
                )}
              </span>
              {!many && (
                <button
                  type="button"
                  disabled={digging}
                  onClick={() => onDig(m.directive, intent, m.item_id ? { archetype: m.who, item_id: m.item_id, scope: 'item' } : null)}
                  title={digging ? 'A dig is going; this will be there when it ends' : m.directive}
                  className="shrink-0 px-2 py-0.5 rounded border border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50"
                >
                  {digging ? 'a dig is going' : intent === 'WIDER' ? 'Dig wider' : 'Dig into this'}
                </button>
              )}
            </li>
          );
        })}
        {missing.length > 40 && (
          <li className="text-xs text-gray-400">and {missing.length - 40} more · “Fill them” works through them all, in order</li>
        )}
      </ul>
      )}
    </section>
  );
}

/** The button that sends a persona at a whole table. Renders nothing when the
 *  table has no holes — which is how a person knows it is filled in. */
export function TableDig({
  dig,
  onDig,
  digging,
  compact = false,
}: {
  dig: BoardroomTableDig | null | undefined;
  onDig: OnDig;
  digging: boolean;
  compact?: boolean;
}) {
  if (!dig) return null;
  return (
    <button
      type="button"
      disabled={digging}
      onClick={() => onDig(dig.directive, 'DEEPER', { archetype: [dig.who, ...(dig.also ?? [])].join(','), item_id: null, scope: 'table' })}
      title={digging ? 'A dig is going; this will be there when it ends' : dig.hint}
      className={`shrink-0 rounded border border-teal-300 dark:border-teal-700 text-teal-800 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50 ${
        compact ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs'
      }`}
      data-testid="table-dig"
    >
      {digging ? 'a dig is going' : `⛏ ${compact ? 'Dig into this table' : dig.label} · ${dig.count}`}
    </button>
  );
}
